import { API_BASE } from "./apiBase.ts";

export type AdministrationRole = {
  id: number;
  code: string;
  name: string;
};

export type AdministrationTenant = {
  id: number;
  code: string;
  name: string;
  roles: AdministrationRole[];
};

export type AdministrationContext = {
  is_company_administrator: boolean;
  tenants: AdministrationTenant[];
};

export type ReportPreview = {
  tenant: AdministrationTenant;
  role: AdministrationRole;
  menu: any[];
};

export type CannedReportAssignment = {
  tenant_id: number;
  tenant_code?: string;
  role_id: number;
  role_code?: string;
  can_view: boolean;
  can_edit: boolean;
  can_schedule: boolean;
};

export type CannedReport = {
  id: number;
  title: string;
  slug: string;
  mb_type: "card" | "dashboard";
  mb_resource_id: number;
  menu_section?: string | null;
  airport_required?: boolean;
  // True when this report was published from a user's saved item. Editing is
  // then governed by creator/curator identity, not the can_edit assignment.
  has_saved_item?: boolean;
  assignments: CannedReportAssignment[];
};

function headers(accessToken: string, email?: string) {
  return {
    Authorization: `Bearer ${accessToken}`,
    ...(email ? { "X-User-Email": email } : {}),
  };
}

export async function getAdministrationContext(
  accessToken: string,
  email?: string,
): Promise<AdministrationContext> {
  const response = await fetch(`${API_BASE}/api/report-administration/context`, {
    headers: headers(accessToken, email),
    credentials: "include",
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`report administration context ${response.status}`);
  }

  return response.json();
}

export async function getReportPreview(
  accessToken: string,
  tenantId: number,
  roleId: number,
  email?: string,
): Promise<ReportPreview> {
  const params = new URLSearchParams({
    tenant_id: String(tenantId),
    role_id: String(roleId),
    _: String(Date.now()),
  });
  const response = await fetch(
    `${API_BASE}/api/report-administration/preview?${params.toString()}`,
    {
      headers: headers(accessToken, email),
      credentials: "include",
      cache: "no-store",
    },
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`report preview ${response.status}: ${message}`);
  }

  return response.json();
}

export async function listCannedReports(accessToken: string, email?: string) {
  const response = await fetch(`${API_BASE}/api/report-administration/reports`, {
    headers: headers(accessToken, email),
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) throw new Error(`canned reports ${response.status}`);
  return response.json() as Promise<{ reports: CannedReport[] }>;
}

export async function upsertCannedReportAssignments(
  accessToken: string,
  reportId: number,
  assignments: CannedReportAssignment[],
  email?: string,
) {
  const response = await fetch(
    `${API_BASE}/api/report-administration/reports/${reportId}/assignments`,
    {
      method: "PUT",
      headers: { ...headers(accessToken, email), "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ assignments }),
    },
  );
  if (!response.ok) throw new Error(`canned report assignments ${response.status}`);
}

export async function deleteCannedReportAssignment(
  accessToken: string,
  reportId: number,
  tenantId: number,
  roleId: number,
  email?: string,
) {
  const response = await fetch(
    `${API_BASE}/api/report-administration/reports/${reportId}/assignments`,
    {
      method: "DELETE",
      headers: { ...headers(accessToken, email), "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ tenant_id: tenantId, role_id: roleId }),
    },
  );
  if (!response.ok) throw new Error(`canned report assignment delete ${response.status}`);
}
